import { FindMode, TextSearchHit } from '../types';

const MIN_QUERY_LEN = 3;
const HIT_CAP = 100;
const SNIPPET_PAD = 48;

export { MIN_QUERY_LEN, HIT_CAP };

export interface PageTextCache {
    pageIndex: number;
    items: Array<{ str: string; start: number; end: number; sourceIndex: number }>;
    text: string;
}

export interface FindHit extends TextSearchHit {
    start: number;
    end: number;
    itemIndices: number[];
    snippet: string;
    snippetMatchStart: number;
    snippetMatchEnd: number;
    soft?: boolean;
    locateRole?: 'value' | 'label';
    mismatch?: boolean;
}

function stripIgnorable(s: string): string {
    return s.replace(/[\u00AD\u200B-\u200D\uFEFF]/g, '');
}

export function normalizeQuery(query: string): string {
    return stripIgnorable(query).replace(/\s+/g, ' ').trim().toLowerCase();
}

function isWordChar(ch: string): boolean {
    if (!ch) {
        return false;
    }
    return /[0-9A-Za-z\u00C0-\u024F]/.test(ch);
}

function buildSnippet(text: string, start: number, end: number): { snippet: string; snippetMatchStart: number; snippetMatchEnd: number } {
    const from = Math.max(0, start - SNIPPET_PAD);
    const to = Math.min(text.length, end + SNIPPET_PAD);
    const prefix = from > 0 ? '…' : '';
    const suffix = to < text.length ? '…' : '';
    const core = text.slice(from, to).replace(/\s+/g, ' ');
    const snippetMatchStart = prefix.length + (start - from);
    const snippetMatchEnd = snippetMatchStart + (end - start);
    return {
        snippet: prefix + core + suffix,
        snippetMatchStart,
        snippetMatchEnd
    };
}

export function buildPageCache(pageIndex: number, textContent: { items: any[] }): PageTextCache {
    const items: Array<{ str: string; start: number; end: number; sourceIndex: number }> = [];
    let text = '';
    for (let i = 0; i < textContent.items.length; i++) {
        const item = textContent.items[i];
        if (!item || typeof item.str !== 'string') {
            continue;
        }
        const str = stripIgnorable(item.str);
        if (!str) {
            continue;
        }
        const start = text.length;
        text += str;
        items.push({ str, start, end: text.length, sourceIndex: i });
    }
    return { pageIndex, items, text: text.toLowerCase() };
}

export async function buildDocumentTextCache(pdf: any): Promise<{ pages: PageTextCache[]; hasText: boolean }> {
    const pages: PageTextCache[] = [];
    let hasText = false;
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const cache = buildPageCache(i - 1, content);
        pages.push(cache);
        if (cache.text.replace(/\s+/g, '').length > 0) {
            hasText = true;
        }
    }
    return { pages, hasText };
}

function findContains(text: string, query: string): Array<{ start: number; end: number }> {
    const out: Array<{ start: number; end: number }> = [];
    if (!query) {
        return out;
    }
    let from = 0;
    while (from <= text.length - query.length) {
        const idx = text.indexOf(query, from);
        if (idx < 0) {
            break;
        }
        out.push({ start: idx, end: idx + query.length });
        from = idx + Math.max(query.length, 1);
    }
    return out;
}

function findWholeWord(text: string, query: string): Array<{ start: number; end: number }> {
    const out: Array<{ start: number; end: number }> = [];
    const parts = query.split(' ').filter(Boolean);
    if (parts.length === 0) {
        return out;
    }
    const joined = parts.join(' ');
    let from = 0;
    while (from <= text.length - joined.length) {
        const idx = text.indexOf(joined, from);
        if (idx < 0) {
            break;
        }
        const before = idx === 0 ? '' : text.charAt(idx - 1);
        const after = idx + joined.length >= text.length ? '' : text.charAt(idx + joined.length);
        if (!isWordChar(before) && !isWordChar(after)) {
            out.push({ start: idx, end: idx + joined.length });
        }
        from = idx + 1;
    }
    return out;
}

function overlappingItems(page: PageTextCache, start: number, end: number): number[] {
    const indices: number[] = [];
    for (let i = 0; i < page.items.length; i++) {
        const it = page.items[i];
        if (it.end > start && it.start < end) {
            indices.push(it.sourceIndex);
        }
    }
    return indices;
}

function pushHitFromRange(
    hits: FindHit[],
    page: PageTextCache,
    start: number,
    end: number,
    matchIndexRef: { n: number },
    locateRole?: 'value' | 'label'
): boolean {
    if (hits.length >= HIT_CAP) {
        return false;
    }
    if (hits.length > 0) {
        const prev = hits[hits.length - 1];
        if (prev.pageIndex === page.pageIndex && prev.start === start && prev.end === end) {
            return true;
        }
    }
    const snip = buildSnippet(page.text, start, end);
    const hit: FindHit = {
        pageIndex: page.pageIndex,
        matchIndex: matchIndexRef.n++,
        start,
        end,
        itemIndices: overlappingItems(page, start, end),
        snippet: snip.snippet,
        snippetMatchStart: snip.snippetMatchStart,
        snippetMatchEnd: snip.snippetMatchEnd
    };
    if (locateRole) {
        hit.locateRole = locateRole;
    }
    hits.push(hit);
    return true;
}

function buildDigitIndex(text: string): { digits: string; map: number[] } {
    let digits = '';
    const map: number[] = [];
    for (let i = 0; i < text.length; i++) {
        const ch = text.charAt(i);
        if (ch >= '0' && ch <= '9') {
            map.push(i);
            digits += ch;
        }
    }
    return { digits, map };
}

function maxDigitSequenceSpan(digitQueryLength: number): number {
    return digitQueryLength + Math.max(2, Math.ceil(digitQueryLength / 3));
}

function findDigitSequence(
    text: string,
    digitQuery: string
): Array<{ start: number; end: number }> {
    const out: Array<{ start: number; end: number }> = [];
    if (!digitQuery || digitQuery.length < 2) {
        return out;
    }
    const indexed = buildDigitIndex(text);
    const maxSpan = maxDigitSequenceSpan(digitQuery.length);
    let from = 0;
    while (from <= indexed.digits.length - digitQuery.length) {
        const idx = indexed.digits.indexOf(digitQuery, from);
        if (idx < 0) {
            break;
        }
        const start = indexed.map[idx];
        const end = indexed.map[idx + digitQuery.length - 1] + 1;
        if (end - start <= maxSpan) {
            out.push({ start, end });
            from = idx + digitQuery.length;
        } else {
            from = idx + 1;
        }
    }
    return out;
}

export function looksLikeSsnQuery(rawQuery: string): boolean {
    const digits = (rawQuery || '').replace(/\D/g, '');
    return digits.length === 4 || digits.length === 9;
}

export function looksLikeMoneyQuery(rawQuery: string): boolean {
    const s = (rawQuery || '').trim();

    if (!s) {
        return false;
    }

    return /^\$?-?\s*\d{1,3}(,\d{3})*(\.\d{2})?$/.test(s)
        || /^\$?-?\s*\d+(\.\d{2})?$/.test(s);
}

function moneyDigitQueries(rawQuery: string): string[] {
    const digits = (rawQuery || '').replace(/\D/g, '');
    const out: string[] = [];
    const add = (v: string) => {
        if (v && v.length >= 2 && out.indexOf(v) < 0) {
            out.push(v);
        }
    };

    add(digits);

    const compact = (rawQuery || '').replace(/[$,\s]/g, '');

    if (/\.\d{2}$/.test(compact) && digits.length > 2 && digits.slice(-2) === '00') {
        add(digits.slice(0, -2));
    }

    return out;
}

function findRegexRanges(text: string, pattern: RegExp): Array<{ start: number; end: number }> {
    const out: Array<{ start: number; end: number }> = [];
    const re = new RegExp(pattern.source, pattern.flags.indexOf('g') >= 0 ? pattern.flags : pattern.flags + 'g');
    let m: RegExpExecArray | null = re.exec(text);
    while (m) {
        out.push({ start: m.index, end: m.index + m[0].length });
        if (m[0].length === 0) {
            re.lastIndex += 1;
        }
        m = re.exec(text);
    }
    return out;
}

function rangesOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
    return a.start < b.end && b.start < a.end;
}

export function collapseOverlappingHits(hits: FindHit[]): FindHit[] {
    if (hits.length < 2) {
        return hits;
    }

    const sorted = hits.slice().sort((a, b) => {
        const page = a.pageIndex - b.pageIndex;

        if (page !== 0) {
            return page;
        }

        const start = (a.start || 0) - (b.start || 0);

        if (start !== 0) {
            return start;
        }

        return (b.end || 0) - (a.end || 0);
    });

    const out: FindHit[] = [];

    for (let i = 0; i < sorted.length; i++) {
        const hit = sorted[i];
        const prev = out[out.length - 1];

        if (prev
            && prev.pageIndex === hit.pageIndex
            && rangesOverlap(
                { start: prev.start, end: prev.end },
                { start: hit.start, end: hit.end }
            )) {
            const prevLen = (prev.end || 0) - (prev.start || 0);
            const hitLen = (hit.end || 0) - (hit.start || 0);

            if (hitLen > prevLen) {
                out[out.length - 1] = hit;
            }

            continue;
        }

        out.push(hit);
    }

    for (let i = 0; i < out.length; i++) {
        out[i] = { ...out[i], matchIndex: i };
    }

    return out;
}

export function searchSsnLocatePatterns(
    pages: PageTextCache[]
): { hits: FindHit[]; truncated: boolean; status: 'ok' } {
    const hits: FindHit[] = [];
    const matchIndexRef = { n: 0 };
    const maxHits = 8;
    let truncated = false;

    for (let p = 0; p < pages.length; p++) {
        const page = pages[p];
        const pageRanges: Array<{ start: number; end: number; role: 'value' | 'label' }> = [];

        const numberRanges = findRegexRanges(
            page.text,
            /\b\d{3}\s*[-–—]\s*\d{2}\s*[-–—]\s*\d{4}\b|\b\d{3}\s+\d{2}\s+\d{4}\b/g
        );
        for (let r = 0; r < numberRanges.length; r++) {
            const range = numberRanges[r];
            if (range.end - range.start > 15) {
                continue;
            }
            pageRanges.push({ start: range.start, end: range.end, role: 'value' });
        }

        const labelRanges = findWholeWord(page.text, 'ssn').concat(
            findContains(page.text, 'ssn:')
        );
        for (let r = 0; r < labelRanges.length; r++) {
            const range = labelRanges[r];
            if (range.end - range.start > 6) {
                continue;
            }
            pageRanges.push({ start: range.start, end: range.end, role: 'label' });
        }

        pageRanges.sort((a, b) => {
            const roleRank = (role: 'value' | 'label') => (role === 'value' ? 0 : 1);
            return (roleRank(a.role) - roleRank(b.role)) || (a.start - b.start);
        });
        const kept: Array<{ start: number; end: number }> = [];
        for (let r = 0; r < pageRanges.length; r++) {
            const candidate = pageRanges[r];
            if (kept.some(k => rangesOverlap(k, candidate))) {
                continue;
            }
            kept.push(candidate);
            if (!pushHitFromRange(hits, page, candidate.start, candidate.end, matchIndexRef, candidate.role)) {
                truncated = true;
                break;
            }
            if (hits.length >= maxHits) {
                truncated = true;
                break;
            }
        }
        if (truncated) {
            break;
        }
    }

    const valueHits = hits.filter(h => h.locateRole === 'value');
    const prioritized = valueHits.length > 0 ? valueHits : hits.filter(h => h.locateRole === 'label');
    prioritized.sort((a, b) => {
        const roleRank = (role?: 'value' | 'label') => (role === 'label' ? 1 : 0);
        return (roleRank(a.locateRole) - roleRank(b.locateRole))
            || (a.pageIndex - b.pageIndex)
            || ((a.start || 0) - (b.start || 0));
    });
    for (let i = 0; i < prioritized.length; i++) {
        prioritized[i].matchIndex = i;
    }
    return { hits: prioritized, truncated, status: 'ok' };
}

function mergeRanges(
    a: Array<{ start: number; end: number }>,
    b: Array<{ start: number; end: number }>
): Array<{ start: number; end: number }> {
    const key = (r: { start: number; end: number }) => `${r.start}:${r.end}`;
    const seen: { [k: string]: boolean } = {};
    const out: Array<{ start: number; end: number }> = [];
    const all = a.concat(b);
    for (let i = 0; i < all.length; i++) {
        const r = all[i];
        const k = key(r);
        if (seen[k]) {
            continue;
        }
        seen[k] = true;
        out.push(r);
    }
    out.sort((x, y) => x.start - y.start || x.end - y.end);
    return out;
}

export function searchDocumentCache(
    pages: PageTextCache[],
    rawQuery: string,
    _mode?: FindMode,
    options?: { minLength?: number; flexibleDigits?: boolean }
): { hits: FindHit[]; truncated: boolean; status: 'ok' | 'too_short' | 'empty_query' } {
    const query = normalizeQuery(rawQuery);
    if (!query) {
        return { hits: [], truncated: false, status: 'empty_query' };
    }
    const minLen = options && options.minLength != null ? options.minLength : MIN_QUERY_LEN;
    if (query.length < minLen) {
        return { hits: [], truncated: false, status: 'too_short' };
    }

    const hits: FindHit[] = [];
    let truncated = false;
    const matchIndexRef = { n: 0 };
    const digitQuery = query.replace(/\D/g, '');
    const moneyQuery = looksLikeMoneyQuery(rawQuery);
    const digitQueries = moneyQuery
        ? moneyDigitQueries(rawQuery)
        : (digitQuery.length >= 4 ? [digitQuery] : []);
    const useDigits = !!(options && options.flexibleDigits && digitQueries.length > 0);

    for (let p = 0; p < pages.length; p++) {
        const page = pages[p];
        const ranges = mergeRanges(findWholeWord(page.text, query), findContains(page.text, query));
        for (let r = 0; r < ranges.length; r++) {
            if (!pushHitFromRange(hits, page, ranges[r].start, ranges[r].end, matchIndexRef)) {
                truncated = true;
                break;
            }
        }
        if (!truncated && useDigits) {
            const digitRanges: Array<{ start: number; end: number }> = [];

            for (let d = 0; d < digitQueries.length; d++) {
                const found = findDigitSequence(page.text, digitQueries[d]);

                for (let r = 0; r < found.length; r++) {
                    digitRanges.push(found[r]);
                }
            }

            for (let r = 0; r < digitRanges.length; r++) {
                if (!pushHitFromRange(hits, page, digitRanges[r].start, digitRanges[r].end, matchIndexRef)) {
                    truncated = true;
                    break;
                }
            }
        }
        if (truncated) {
            break;
        }
    }

    return { hits, truncated, status: 'ok' };
}

export function applyHitHighlights(
    textLayer: HTMLElement,
    textDivs: HTMLElement[],
    pageHits: FindHit[],
    activeHit: FindHit | null
): HTMLElement | null {
    for (let i = 0; i < textDivs.length; i++) {
        textDivs[i].classList.remove('findHit', 'findHitActive', 'findHitSoft', 'findHitMismatch');
    }
    let activeEl: HTMLElement | null = null;
    for (let h = 0; h < pageHits.length; h++) {
        const hit = pageHits[h];
        const isActive = !!(activeHit && activeHit.pageIndex === hit.pageIndex && activeHit.matchIndex === hit.matchIndex);
        const indices = hit.itemIndices || [];
        for (let j = 0; j < indices.length; j++) {
            const el = textDivs[indices[j]];
            if (!el) {
                continue;
            }
            el.classList.add(hit.soft && !isActive ? 'findHitSoft' : 'findHit');
            if (isActive) {
                el.classList.add('findHitActive');
                if (!activeEl) {
                    activeEl = el;
                }
            }
        }
    }
    return activeEl;
}
